import {defineStore} from 'pinia'
import {client} from "@/libraries/http";
import useAuthnStore from "@/store/authn";
import idGenerate from "@/libraries/id-generate";
import useDpApplicationConstructionTimeStore from "@/store/dp-application-construction-time-store";
import useConstructionLocationPickerStore from "@/store/construction-location-picker-store";
import useProfitCenterPickerStore from "@/store/profit-center-picker-store";
import store from "store";
import useDpApplicationConstructionTime from "@/store/dp-application-construction-time-store";

/**
 * 根据模板字段生成表单字段
 *
 * @param templateFields
 * @returns {*}
 */
const fieldsOf = (templateFields) => templateFields.map(field => ({...field}));

/**
 *  获取表单中出入证类型
 *
 *  项目-利： PROJECT_ID
 *  施工类型： CONSTRUCTION_TYPE
 *  施工位置： CONSTRUCTION_LOCATION
 *  施工单位： CONSTRUCTOR
 *  施工内容： CONSTRUCTION_CONTENT
 *  施工开始： CONSTRUCTION_START_TIME
 *  施工结束： CONSTRUCTION_END_TIME
 *  人员姓名： PERSONAL_NAME
 *  手机号码： PHONE_NUMBER
 *  身份证正： ID_CARD_PHOTO_FRONT
 *  身份证背： ID_CARD_PHOTO_BACK
 *  人员照片： PERSONAL_PHOTO
 *  租户公司： TENANT_COMPANY
 *  tp加班： TP_OVERTIME_APPROVAL_PHOTO
 *  图片列表： PHOTOS
 *
 * @param fields
 * @param type
 * @returns {*}
 */
const getFieldByType = (fields, type) => {
    return fields.find(field => field.attributeType === type);
}

/**
 * 将当前表单中的字段值按类型赋值到新的字段上
 *
 * @param field 要赋值的字段
 * @param referenceFields 被复制的字段, 如果类型一致则将值赋给 field
 * @returns {*}
 */
const copyValueWhenSameType = (field, referenceFields) => {
    field.value = getFieldByType(referenceFields, field.attributeType)?.value
    return field
}

const useDpApplicationForm = defineStore('dp-application-form', {
    state: () => ({
        templateFields: [],
        commonForm: null,
        applicantForms: [],
        currentApplicationSituationId: null,
        isCache: false,
    }),
    getters: {
        /**
         * 出入证类型
         *
         * 服务商施工: CONSTRUCTION_SERVICE_PROVIDER
         * 装修施工: CONSTRUCTION_DECORATION
         * 营运单元施工: CONSTRUCTION_OPERATIONS
         * 租户人员: TENANT_EMPLOYEE
         *
         * @param state
         * @returns {*|string|string}
         */
        passCardType(state) {
            const authn = useAuthnStore()
            if (authn.isTenant) {
                return 'TENANT_EMPLOYEE'
            }
            const commonForm = state.commonForm;
            if (!commonForm) {
                return 'CONSTRUCTION_DECORATION'
            }
            return getFieldByType(commonForm.fields, 'CONSTRUCTION_TYPE')
                ?.value || 'CONSTRUCTION_DECORATION'
        },
        projectId() {
            if (!this.commonForm) {
                return null
            }
            return getFieldByType(this.commonForm.fields, 'PROJECT_ID')?.value?.projectId
        },
        commonTemplateFields(state) {
            return state.templateFields.filter(field => field.common)
        },
        nonCommonTemplateFields(state) {
            return state.templateFields.filter(field => !field.common)
        },
        initialized(state) {
            return state.commonForm || state.applicantForms?.length > 0
        }
    },
    actions: {
        init(applicationSituationId) {
            let formPromise = Promise.resolve();
            if (applicationSituationId) {
                formPromise = client.get(`/api/h5/digital-passes/applications/${applicationSituationId}/form`)
                    .then(res => {
                        this.commonForm = res.data.commonForm
                        this.applicantForms = res.data.applicantForms
                    })
            }

            formPromise.then(() => {
                this.initTemplate()
            })

            // formPromise.then(() =>
            //     client.get(
            //         `/api/h5/digital-passes/application-templates/${this.passCardType}/fields`,
            //         {params: {projectId: this.projectId}}
            //     )
            // ).then(res => {
            //     this.templateFields = res.data;
            // }).then(() => {
            //     this.initForm()
            //     this.fillTenantFields();
            // })
        },
        initTemplate() {
            client.get(
                `/api/h5/digital-passes/application-templates/${this.passCardType}/fields`,
                {params: {projectId: this.projectId}}
            ).then(res => {
                this.templateFields = res.data.fields;
                useDpApplicationConstructionTimeStore().initLimit(res.data.limitedDuration)
            }).then(() => {
                this.initForm()
                this.fillTenantFields();
            })
        },
        /**
         * 根据模板生成表单, 并且如果当前表单有值，赋值到新的表单中类型相同的字段
         */
        initForm() {
            // 生成表单并赋值
            this.commonForm = {
                fields: fieldsOf(this.commonTemplateFields)
                    .map(field => this.commonForm ? copyValueWhenSameType(field, this.commonForm.fields) : field)
            }

            if (!this.applicantForms) {
                this.applicantForms = []
                // this.applicantForms = [{
                //     id: idGenerate(),
                //     fields: fieldsOf(this.nonCommonTemplateFields)
                // }]
            }

            // 生成表单并赋值
            this.applicantForms = this.applicantForms
                ?.map(form => {
                    form = {
                        id: idGenerate(),
                        fields: fieldsOf(this.nonCommonTemplateFields)
                            .map(field => copyValueWhenSameType(field, form.fields))
                    }
                    return form;
                })
        },
        fillTenantFields() {
            const authn = useAuthnStore()
            if (!authn.isTenant) return;
            // eslint-disable-next-line no-debugger
            // debugger
            getFieldByType(this.commonForm.fields, 'PERSONAL_NAME').value = authn.leoParams?.username
            getFieldByType(this.commonForm.fields, 'PHONE_NUMBER').value = authn.leoParams?.phoneNumber
            getFieldByType(this.commonForm.fields, 'TENANT_COMPANY').value = authn.leoParams?.tenantName
            getFieldByType(this.commonForm.fields, 'BRAND').value = authn.leoParams?.brand
        },
        reset() {
            this.commonForm = null
            this.applicantForms = []
            this.currentApplicationSituationId = null
            useConstructionLocationPickerStore().reset()
            useDpApplicationConstructionTimeStore().reset()
            useProfitCenterPickerStore().reset()
        },
        cache() {
            if (!this.isCache) return;
            console.log("application-cache...")
            store.set('application-cache', {
                userPhone: useAuthnStore().getUserPhone,
                commonForm: this.commonForm,
                applicantForms: this.applicantForms
            });
        },
        loadCache(applicationSituationId) {
            this.isCache = !applicationSituationId
            if (!this.isCache) return;
            console.log("loadCache...")
            let info = store.get('application-cache')
            if (info && info.userPhone === useAuthnStore().getUserPhone) {
                this.commonForm = info.commonForm
                this.applicantForms = info.applicantForms

                let project = getFieldByType(this.commonForm.fields, 'PROJECT_ID')?.value
                if (project) {
                    useProfitCenterPickerStore().pickIfNull(project)
                }

                let startField = getFieldByType(this.commonForm.fields, 'CONSTRUCTION_START_TIME')
                let endField = getFieldByType(this.commonForm.fields, 'CONSTRUCTION_END_TIME')
                if (startField?.value) {
                    const minDate = new Date();
                    minDate.setHours(minDate.getHours() + 1)
                    minDate.setMinutes(0)
                    minDate.setSeconds(0)
                    minDate.setMilliseconds(0)
                    if (minDate.getTime() > new Date(startField?.value).getTime()) {
                        startField.value = null
                        endField.value = null
                    } else {
                        useDpApplicationConstructionTime().setStartTime(new Date(startField?.value))
                        useDpApplicationConstructionTime().setEndTime(new Date(endField?.value))
                    }
                }
            }
        },
        clearCache() {
            if (!this.isCache) return;
            console.log("clearCache...")
            this.isCache = false
            store.remove('application-cache')
        },
        closeCache() {
            this.isCache = false
        },
        clearDate(){
            console.log("clearDate...")
            if (getFieldByType(this.commonForm.fields, 'CONSTRUCTION_START_TIME') != null) {
                getFieldByType(this.commonForm.fields, 'CONSTRUCTION_START_TIME').value = null
            }
            if (getFieldByType(this.commonForm.fields, 'CONSTRUCTION_END_TIME') != null) {
                getFieldByType(this.commonForm.fields, 'CONSTRUCTION_END_TIME').value = null
            }
        }
    },
})

export default useDpApplicationForm;
